import { defineComponent as _defineComponent } from 'vue'
import {ref, watch, onMounted, Ref, getCurrentInstance} from 'vue';
import { useTranslations } from "@/hooks/useTranslations";
import { useNotify } from "@/hooks/useNotify";
import VModal from "@/components/vModal.vue";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import { getErrorMessage } from "@/utils/Helpers";
import { IngredientCost } from "@/models/IngredientCost/IngredientCost";
import {ModifyIngredientCostDto} from "@/models/RecipeInvoice/ModifyIngredientCostDto";
import VAddVendorModal from "@/components/costing/vAddVendorModal.vue";
import VAdditionalMeasurementModal from "@/components/costing/vAdditionalMeasurementModal.vue";
import {Vendor} from "@/models/IngredientCost/Vendor";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";

type vModifyIngredientCostProps = {
  showModifyIngredientCostModal: boolean,
  ingredientCost: IngredientCost
}

export default /*#__PURE__*/_defineComponent({
  __name: 'vModifyIngredientCost',
  props: {
    showModifyIngredientCostModal: { type: Boolean },
    ingredientCost: null
  },
  emits: ['on-cancel', 'save'],
  setup(__props: any, { emit }) {

const props = __props as {
  showModifyIngredientCostModal: boolean,
  ingredientCost: IngredientCost
};



const translations = useTranslations();
const notify = useNotify();

const localIngredientCost = ref<IngredientCost>({
  id: 0,
  name: '',
  cost: null,
  restaurantId: 0,
  vendor: { id: 0, name: '' } as Vendor,
  quantity: null,
  ingredientCostMeasurement: {
    measurement: {
      conversionCoeficient: 0,
      isDefault: false,
      measurementCategory: MeasurementCategory.Mass,
      name: "",
      id: 0,
    },
    weightInGrams: 0,
    measurementId: 0,
    ingredientCostId: 0,
    createdOn: null,
    id: 0
  },
  ingredientCostMeasurements: [],
});

const vendors = ref<any[]>([]);
const showAddingVendorModal = ref(false);
const showAddingMeasurementrModal = ref(false);
// const selectedIngredientCost: Ref<IngredientCost> = ref(null);

watch(
  () => props.ingredientCost,
  (newVal) => {
    localIngredientCost.value = newVal ? { ...newVal } : { ...localIngredientCost.value };
  },
  { immediate: true }
);

onMounted(() => {
  loadVendors();
  //loadMeasurements();
});

function translate(key: string) {
  return translations.value.get(key);
}

function closeModal() {
  emit("on-cancel");
}

async function saveIngredientCost() {
  const isEdit = Boolean(props.ingredientCost.id);
  const dto = isEdit
    ? toModifyDto(localIngredientCost.value)
    : toCreateDto(localIngredientCost.value);

  try {
    if (isEdit) {
      await IngredientCostRepository.updateIngredientCost(dto);
    }
    else {
      await IngredientCostRepository.createIngredientCosts([dto]);
    }

    emit("save", localIngredientCost.value);
    notify.success(translations.value.get("ms_save_success"), { closeOnClick: true, timeout: 3500 });
    closeModal();
  }
  catch (error) {
    notify.error("Please fix the errors.", { closeOnClick: true, timeout: 3500 });
  }
}

function toCreateDto(cost: IngredientCost): ModifyIngredientCostDto {
  return {
    name: cost.name,
    vendorId: cost.vendor.id,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName:
      cost.ingredientCostMeasurement.measurement.name,
    ingredientCostMeasurements: cost.ingredientCostMeasurements.map((m: any) => ({
      id: m.id,
      ingredientCostId: m.ingredientCostId,
      measurementId: m.measurementId,
      name: m.measurement.name || "",
      weightInGrams: m.weightInGrams,
    })),
  };
}

function toModifyDto(cost: AdditionalCost) {
  return {
    id: cost.id,
    name: cost.name,
    vendorId: cost.vendor.id,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName: String(
      cost.ingredientCostMeasurement.measurement.name || ""
    ),
    ingredientCostMeasurements: cost.ingredientCostMeasurements.map((m) => ({
      id: m.id || 0,
      ingredientCostId: cost.id || 0,
      measurementId: m.measurement.id || 0,
      name: m.measurement.name || "",
      weightInGrams: m.weightInGrams
    }))
  };
}

function openAddVendorModal() {
  showAddingVendorModal.value = true;
}

function handleVendorAdded(vendorList: Vendor[]) {
  vendors.value = vendorList;
}

function openAddMeasurementModal() {
  // fetchAdditionalMeasurementsForCost();
  showAddingMeasurementrModal.value = true;
}

function removeRow(index: number) {
  localIngredientCost.value.splice(index, 1);
}

function loadVendors() {
  if (!vendors.value.length) {
    IngredientCostRepository.listIngredientCostVendors()
      .then((res) => vendors.value = res.data.result)
      .catch((err) => notify.error(getErrorMessage(err, translations)));
  }
}

function handleSaveMeasurements(newMeasurements: any[]) {
  let measurementNames = localIngredientCost.value.ingredientCostMeasurements.map(
    (x) => x.measurement.name
  );

  newMeasurements.map((x) => {
    if (!measurementNames.includes(x.name)) {
      localIngredientCost.value.ingredientCostMeasurements.push({
        measurement: {
          conversionCoeficient: 0,
          isDefault: false,
          measurementCategory: MeasurementCategory.Mass,
          name: x.name,
          id: 0,
        },
        weightInGrams: x.weight,
        measurementId: 0,
        ingredientCostId: 0,
        createdOn: null,
        id: 0
      });
    }
    return x;
  });

  showAddingMeasurementrModal.value = false;
}

return { __sfc: true,props, emit, translations, notify, localIngredientCost, vendors, showAddingVendorModal, showAddingMeasurementrModal, translate, closeModal, saveIngredientCost, toCreateDto, toModifyDto, openAddVendorModal, handleVendorAdded, openAddMeasurementModal, removeRow, loadVendors, handleSaveMeasurements, VModal, VAddVendorModal, VAdditionalMeasurementModal }
}

})